import React from "react";
import { Link } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiClockOutline, mdiCalendarOutline, mdiMapMarker } from "@mdi/js";
import { slugify } from "../../../../utils/slugify";

function stripHtml(html) {
  return String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function estimateReadTime(html) {
  const words = stripHtml(html).split(" ").filter(Boolean).length;
  return `${Math.max(1, Math.ceil(words / 200))} min read`;
}

function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export default function BlogListItemGem({ blog, badgeLabel = "Blog", badgeIconPath, delay = "0ms", index = 0 }) {
  const blogSlug = blog?.slug || slugify(blog?.title || "");
  const blogUrl = blogSlug ? `/blog/${blogSlug}` : `/blog/${blog?.id}`;
  const image = blog?.images;
  const excerpt = blog?.short_description || stripHtml(blog?.description).slice(0, 150);
  const readTime = estimateReadTime(blog?.description);
  const date = formatDate(blog?.created_at);
  const isReversed = index % 2 === 1;

  return (
    <article
      className="journey-card"
      style={{
        animationDelay: delay,
        flexDirection: isReversed ? "row-reverse" : "row",
      }}
    >
      {/* Connecting route dot */}
      <div className="journey-card__node">
        <div className="journey-card__node-dot" />
        <div className="journey-card__node-line" />
      </div>

      {/* Polaroid-style image */}
      <div className="journey-card__polaroid">
        <div className="journey-card__photo">
          <img
            src={image}
            alt={blog?.title || "Blog"}
            onError={(e) => { e.currentTarget.style.background = "#EEF7F7"; e.currentTarget.style.display = "none"; }}
          />
          <div className="journey-card__photo-badge">
            {badgeIconPath && <Icon path={badgeIconPath} size={0.5} />}
            {badgeLabel}
          </div>
        </div>
        {/* Postmark stamp decoration */}
        <div className="journey-card__stamp">
          <svg viewBox="0 0 40 40" width="40" height="40">
            <circle cx="20" cy="20" r="17" fill="none" stroke="rgba(224,90,73,0.15)" strokeWidth="1" strokeDasharray="3,2" />
            <circle cx="20" cy="20" r="12" fill="none" stroke="rgba(224,90,73,0.12)" strokeWidth="0.5" />
            <text x="20" y="22" textAnchor="middle" fontSize="7" fill="rgba(224,90,73,0.2)" fontFamily="Georgia, serif" transform="rotate(-15, 20, 20)">✈</text>
          </svg>
        </div>
      </div>

      {/* Content side */}
      <div className="journey-card__content">
        {/* Date & Read time */}
        <div className="journey-card__meta">
          {date && (
            <span className="journey-card__meta-date">
              <Icon path={mdiCalendarOutline} size={0.45} />
              {date}
            </span>
          )}
          {readTime && (
            <span className="journey-card__meta-read">
              <Icon path={mdiClockOutline} size={0.45} />
              {readTime}
            </span>
          )}
        </div>

        {/* Title with map pin */}
        <Link to={blogUrl} className="journey-card__title">
          <Icon path={mdiMapMarker} size={0.55} className="journey-card__pin-icon" />
          {blog?.title || "Untitled"}
        </Link>

        {/* Excerpt */}
        <p className="journey-card__excerpt">"{excerpt}"</p>

        {/* CTA */}
        <Link to={blogUrl} className="journey-card__cta">
          <span>Read the story</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
          <div className="journey-card__cta-glide" />
        </Link>
      </div>
    </article>
  );
}